import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api';
import { useLocation, useNavigate } from 'react-router-dom'
import { 
  FileText, 
  BarChart3, 
  Download,
  Send,
  Users as UsersIcon,
  RefreshCw
} from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingOverlay from '../components/LoadingOverlay'
import HelpButton from '../components/HelpButton'
import DashboardNewsBanner from '../components/DashboardNewsBanner'
import { usePageTour } from '../hooks/usePageTour'
import {
  usuarioEnSesionId,
  novedadFirmaGuiadaPendiente,
} from '../config/newsTours'
import { anuncioPendientePara } from '../config/releaseAnnouncements'
import '../styles/layout.css'
import '../styles/dashboard.css'

const Dashboard = () => {
  const [user, setUser] = useState(null)
  const [stats, setStats] = useState(null)
  const [recentSignatures, setRecentSignatures] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null)
  const [mesesSeleccionados, setMesesSeleccionados] = useState(6)
  const [newsTick, setNewsTick] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()
  const userId = usuarioEnSesionId()
  const novedadPendiente = novedadFirmaGuiadaPendiente(userId)
  const anuncioPendiente = Boolean(anuncioPendientePara(userId))

  usePageTour('dashboard-page', {
    enabled: !isLoading && !novedadPendiente && !anuncioPendiente,
    syncKey: newsTick,
  })

  useEffect(() => {
    const refrescarAviso = () => setNewsTick((n) => n + 1)
    window.addEventListener('app-news:closed', refrescarAviso)
    window.addEventListener('app-release:closed', refrescarAviso)
    return () => {
      window.removeEventListener('app-news:closed', refrescarAviso)
      window.removeEventListener('app-release:closed', refrescarAviso)
    }
  }, [])
  
  // Verificar permisos: supervisor no puede descargar documentos
  const isSupervisor = user?.role === 'supervisor'
  const isAdmin = user?.role === 'administrador' || user?.is_admin

  // Función para obtener URL de firma
  const getFirmarUrl = (tokenFirma) => {
    return `${window.location.origin}/registrar/${tokenFirma}`
  }

  // Función para compartir por WhatsApp
  const compartirPorWhatsApp = (tokenFirma, nombreFirmante, tituloDocumento) => {
    const linkFirma = getFirmarUrl(tokenFirma)
    const mensaje = `Hola ${nombreFirmante},\n\nTe invito a firmar el documento: ${tituloDocumento}\n\nPara firmar, haz clic en el siguiente enlace:\n${linkFirma}\n\nSaludos`
    const urlWhatsApp = `https://wa.me/?text=${encodeURIComponent(mensaje)}`
    window.open(urlWhatsApp, '_blank')
  }

  useEffect(() => {
    // CRÍTICO: NO cargar Dashboard si el modal de compartir está abierto
    const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
    if (savedShowModal === 'true') {
      console.log('[Dashboard] 🚫 BLOQUEANDO carga de Dashboard - modal de compartir está abierto')
      return
    }

    // Verificar autenticación - verificar ambos storages
    let token = localStorage.getItem('token') || sessionStorage.getItem('token')
    let userData = localStorage.getItem('user') || sessionStorage.getItem('user')
    
    if (!token || !userData) {
      // NO redirigir si el modal está abierto
      if (savedShowModal !== 'true') {
      window.location.href = '/login'
      }
      return
    }

    setUser(JSON.parse(userData))
    loadDashboardData()
  }, [location.pathname, mesesSeleccionados]) // Recargar cuando cambia la ruta o los meses seleccionados

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      
      // Verificar ambos storages para obtener token y datos del usuario
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const userDataStr = localStorage.getItem('user') || sessionStorage.getItem('user')
      const userData = JSON.parse(userDataStr || '{}')
      const isAdmin = userData.role === 'administrador' || userData.is_admin
      const isSupervisor = userData.role === 'supervisor'
      
      const headers = {
        'Authorization': `Bearer ${token}`
      }
      
      // Todos los usuarios pueden ver sus estadísticas personales
      const [userStatsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/firmas/estadisticas-usuario?meses=${mesesSeleccionados}`, { headers })
      ])
      
      let userStats = null
      if (userStatsResponse.ok) {
        userStats = await userStatsResponse.json()
        console.log('[DEBUG Dashboard] Estadísticas del usuario:', userStats)
      } else {
        console.error('[DEBUG Dashboard] Error al obtener estadísticas del usuario:', userStatsResponse.status)
      }
      
      // Cargar firmas recientes para todos los usuarios (sus propias firmas y documentos enviados)
      const signaturesResponse = await fetch(`${API_BASE_URL}/api/firmas?limit=5`, { headers })
      
      if (signaturesResponse.ok) {
        const signaturesData = await signaturesResponse.json()
        console.log('📥 Firmas recientes recibidas:', signaturesData.firmas)
        signaturesData.firmas?.forEach((firma, index) => {
          console.log(`  ${index + 1}. ID: ${firma.id}, Título: ${firma.documento_titulo}, Firmantes:`, firma.firmantes)
        })
        setRecentSignatures(signaturesData.firmas || [])
      } else if (signaturesResponse.status === 403) {
        console.log('Usuario no tiene permisos para ver firmas')
      }
      
      // Administradores y supervisores pueden ver estadísticas completas
      if (isAdmin || isSupervisor) {
        const statsResponse = await fetch(`${API_BASE_URL}/api/firmas/estadisticas?meses=${mesesSeleccionados}`, { headers })
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json()
          // Combinar estadísticas del admin con estadísticas del usuario
          // Para documentos_enviados, usar siempre las estadísticas del usuario (sus propios documentos)
          setStats({
            ...statsData,
            documentos_pendientes_usuario: userStats?.documentos_pendientes || 0,
            documentos_firmados_usuario: userStats?.documentos_firmados || 0,
            documentos_enviados: userStats?.documentos_enviados || 0,  // Siempre usar las estadísticas del usuario para documentos enviados
            firmantes_pendientes_lista: userStats?.firmantes_pendientes_lista || []  // Incluir lista de firmantes pendientes del usuario
          })
        } else if (statsResponse.status === 403) {
          // Usuario no tiene permisos, usar solo estadísticas de usuario
          console.log('Usuario no tiene permisos para ver estadísticas completas, usando estadísticas personales')
          setStats({
            documentos_pendientes: userStats?.documentos_pendientes || 0,
            documentos_firmados: userStats?.documentos_firmados || 0,
            documentos_enviados: userStats?.documentos_enviados || 0,
            documentos_por_mes: userStats?.documentos_por_mes || [],
            firmas_por_dia: userStats?.firmas_por_dia || [],
            firmantes_pendientes: userStats?.firmantes_pendientes || 0,
            firmantes_firmados: userStats?.firmantes_firmados || 0,
            firmantes_pendientes_lista: userStats?.firmantes_pendientes_lista || [],
            tiempo_promedio_firma_horas: userStats?.tiempo_promedio_firma_horas || null,
            top_firmantes: userStats?.top_firmantes || []
          })
        }
      } else {
        // Usuario normal: mostrar sus estadísticas personales incluyendo gráficos
        console.log('[DEBUG Dashboard] Usuario normal - Estadísticas:', userStats)
        setStats({
          documentos_pendientes: userStats?.documentos_pendientes || 0,
          documentos_firmados: userStats?.documentos_firmados || 0,
          documentos_enviados: userStats?.documentos_enviados || 0,
          documentos_por_mes: userStats?.documentos_por_mes || [],
          firmas_por_dia: userStats?.firmas_por_dia || [],
          firmantes_pendientes: userStats?.firmantes_pendientes || 0,
          firmantes_firmados: userStats?.firmantes_firmados || 0,
          firmantes_pendientes_lista: userStats?.firmantes_pendientes_lista || [],
          tiempo_promedio_firma_horas: userStats?.tiempo_promedio_firma_horas || null,
          top_firmantes: userStats?.top_firmantes || []
        })
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error)
      toast.error('Error al cargar datos del dashboard')
    } finally {
      setIsLoading(false)
      setUltimaActualizacion(new Date())
    }
  }

  const handleRefresh = async () => {
    if (refreshing) return
    setRefreshing(true)
    try {
      await loadDashboardData()
    } finally {
      setRefreshing(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES')
  }

  if (isLoading) {
    return <LoadingOverlay message="Cargando dashboard..." />
  }

  return (
    <div className="min-h-screen" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-title">
              <h1>Dashboard</h1>
              <p>Sistema de Firma Electrónica</p>
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {ultimaActualizacion && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #9ca3af)' }}>
                Actualizado: {ultimaActualizacion.toLocaleTimeString('es-CO', { timeStyle: 'short' })}
              </span>
            )}
            <button
              id="tour-dashboard-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Actualizar dashboard"
              aria-busy={refreshing}
              title="Actualizar datos"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '8px', fontSize: '0.8rem',
                fontWeight: 500,
                border: refreshing ? '1px solid #e5e7eb' : '1px solid #bfdbfe',
                background: refreshing ? '#f9fafb' : 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                color: refreshing ? '#9ca3af' : '#2563eb',
                transition: 'all 0.2s'
              }}
            >
              <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              <span>{refreshing ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        <DashboardNewsBanner userId={userId} onUpdate={() => setNewsTick((n) => n + 1)} />
        {/* Estadísticas principales — tour: #tour-dashboard-stats = Firmados + Pendientes + Enviados */}
        {stats && (
          <>
            <div id="tour-dashboard-stats" className="dashboard-grid">
              <div className="stat-card text-center">
                <div className="stat-card-icon" style={{background: '#E0E7FF', color: '#667eea'}}>
                  <FileText size={24} />
                </div>
                <div className="stat-card-value" style={{color: '#667eea'}}>
                  {(isAdmin || isSupervisor) 
                    ? (stats.documentos_firmados || 0)
                    : (stats.documentos_firmados || stats.documentos_firmados_usuario || 0)
                  }
                </div>
                <div className="stat-card-label">
                  {(isAdmin || isSupervisor) ? 'Todos los Documentos Firmados' : 'Mis Documentos Firmados'}
                </div>
              </div>
              <div 
                className="stat-card text-center" 
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/documentos-pendientes')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <div className="stat-card-icon" style={{background: '#FEF3C7', color: '#f59e0b'}}>
                  <BarChart3 size={24} />
                </div>
                <div className="stat-card-value" style={{color: '#f59e0b'}}>
                  {(isAdmin || isSupervisor)
                    ? (stats.pendientes || 0)
                    : (stats.documentos_pendientes || stats.documentos_pendientes_usuario || 0)
                  }
                </div>
                <div className="stat-card-label">
                  {(isAdmin || isSupervisor) ? 'Todos los Documentos Pendientes' : 'Mis Documentos Pendientes'}
                </div>
              </div>
              <div className="stat-card text-center">
                <div className="stat-card-icon" style={{background: '#DCFCE7', color: '#16a34a'}}>
                  <Send size={24} />
                </div>
                <div className="stat-card-value" style={{color: 'var(--text-primary)'}}>{stats.documentos_enviados ?? 0}</div>
                <div className="stat-card-label">Documentos Enviados</div>
              </div>
            </div>
            {(stats.usuarios_activos !== undefined) && (
              <div className="dashboard-grid">
                <div className="stat-card text-center">
                  <div className="stat-card-icon" style={{background: '#DBEAFE', color: '#3b82f6'}}>
                    <UsersIcon size={24} />
                  </div>
                  <div className="stat-card-value" style={{color: '#3b82f6'}}>{stats.usuarios_activos || 0}</div>
                  <div className="stat-card-label">Usuarios Registrados</div>
                </div>
              </div>
            )}
            {(stats.documentos_totales !== undefined || stats.documentos_en_proceso !== undefined) && (
              <div className="dashboard-grid">
                {(stats.documentos_totales !== undefined) && (
                  <div className="stat-card text-center">
                    <div className="stat-card-value" style={{color: 'var(--text-primary)'}}>{stats.documentos_totales || 0}</div>
                    <div className="stat-card-label">Documentos Totales</div>
                  </div>
                )}
                {(stats.documentos_en_proceso !== undefined) && (
                  <div className="stat-card text-center">
                    <div className="stat-card-value" style={{color: '#6366f1'}}>{stats.documentos_en_proceso || 0}</div>
                    <div className="stat-card-label">En Proceso</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Documentos por Mes */}
        <div id="tour-dashboard-chart" className="mt-8">
          <div className="dashboard-chart-card">
            <div className="dashboard-chart-header">
              <h3 className="dashboard-chart-title">Documentos por Mes</h3>
              <div className="flex items-center gap-3">
                <select
                  value={mesesSeleccionados}
                  onChange={(e) => setMesesSeleccionados(parseInt(e.target.value))}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                >
                  <option value={1}>Último mes</option>
                  <option value={2}>Últimos 2 meses</option>
                  <option value={3}>Últimos 3 meses</option>
                  <option value={4}>Últimos 4 meses</option>
                  <option value={5}>Últimos 5 meses</option>
                  <option value={6}>Últimos 6 meses</option>
                </select>
              </div>
            </div>
            {stats ? (
              <LineChart data={stats.documentos_por_mes || []} />
            ) : (
              <div className="h-56 flex items-center justify-center text-gray-500">Sin datos</div>
            )}
          </div>
        </div>
            
        {stats && (
          <div id="tour-dashboard-firmantes" className="mt-8">
            <div className="dashboard-chart-card">
              <div className="dashboard-chart-header">
                <h3 className="dashboard-chart-title">Firmantes</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-sm text-gray-600">Firmados: <strong className="text-green-600">{stats.firmantes_firmados || 0}</strong></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-sm text-gray-600">Pendientes: <strong className="text-yellow-600">{stats.firmantes_pendientes || 0}</strong></span>
                  </div>
                </div>
              </div>
              
              {/* Lista de firmantes pendientes */}
              {Array.isArray(stats.firmantes_pendientes_lista) && stats.firmantes_pendientes_lista.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-yellow-500">⚠️</span>
                    Firmantes Pendientes ({stats.firmantes_pendientes_lista.length})
                  </h4>
                  <div className="space-y-3">
                    {stats.firmantes_pendientes_lista.map((firmante) => (
                      <div 
                        key={firmante.id} 
                        className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                {firmante.nombre.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-gray-900 truncate">{firmante.nombre}</p>
                                <p className="text-sm text-gray-600 flex items-center gap-1 truncate">
                                  <span>📧</span>
                                  <span className="truncate">{firmante.email}</span>
                                </p>
                              </div>
                            </div>
                            <div className="ml-12 mt-2">
                              <p className="text-sm text-gray-700 break-words">
                                <span className="font-medium">Documento:</span> {firmante.documento_titulo}
                              </p>
                              {firmante.fecha_invitacion && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Invitado: {new Date(firmante.fecha_invitacion).toLocaleDateString('es-ES')}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token') || sessionStorage.getItem('token')
                                  const response = await fetch(`${API_BASE_URL}/api/firmas/enviar-recordatorio/${firmante.id}`, {
                                    method: 'POST',
                                    headers: {
                                      'Authorization': `Bearer ${token}`
                                    }
                                  })
                                  
                                  if (response.ok) {
                                    const result = await response.json()
                                    toast.success(result.message || 'Recordatorio enviado exitosamente')
                                  } else {
                                    const error = await response.json()
                                    toast.error(error.detail || 'Error al enviar recordatorio')
                                  }
                                } catch (error) {
                                  console.error('Error al enviar recordatorio:', error)
                                  toast.error('Error al enviar recordatorio')
                                }
                              }}
                              className="w-full sm:w-auto sm:flex-shrink-0 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-sm hover:shadow-md whitespace-nowrap"
                              title="Enviar recordatorio"
                            >
                              <span>📬</span>
                              <span className="hidden sm:inline">Enviar Recordatorio</span>
                              <span className="sm:hidden">Enviar</span>
                            </button>
                            {firmante.token_firma && (
                              <button
                                onClick={() => compartirPorWhatsApp(firmante.token_firma, firmante.nombre, firmante.documento_titulo)}
                                className="w-full sm:w-auto sm:flex-shrink-0 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-sm hover:shadow-md whitespace-nowrap"
                                title="Compartir enlace por WhatsApp"
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                <span className="hidden sm:inline">WhatsApp</span>
                                <span className="sm:hidden">WA</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Top Firmantes — ancla tour siempre en DOM */}
              <div id="tour-dashboard-top-firmantes" className="mt-6 pt-6 border-t border-gray-200">
                {Array.isArray(stats.top_firmantes) && stats.top_firmantes.length > 0 ? (
                  <>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="text-green-500">🏆</span>
                      Top Firmantes
                    </h4>
                    <div className="space-y-2">
                      {stats.top_firmantes.map((f, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-sm transition-all">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                              {i + 1}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{f.nombre || f.email}</p>
                              <p className="text-xs text-gray-600">{f.email}</p>
                            </div>
                          </div>
                          <span className="font-bold text-green-600 bg-white px-3 py-1 rounded-full shadow-sm">{f.total} Documentos Firmados</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
              
              {/* Mensaje si no hay firmantes pendientes */}
              {(!Array.isArray(stats.firmantes_pendientes_lista) || stats.firmantes_pendientes_lista.length === 0) && (
                <div className="mt-6 p-6 bg-green-50 rounded-lg border border-green-200 text-center">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-gray-700 font-medium">¡Excelente! No hay firmantes pendientes</p>
                  <p className="text-sm text-gray-600 mt-1">Todos los documentos han sido firmados</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Firmas Recientes */}
        <div id="tour-dashboard-firmas-recientes" className="mt-8">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Firmas Recientes</h3>
            {recentSignatures.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No hay firmas recientes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentSignatures.map((signature) => (
                  <div key={signature.id} className="activity-item">
                    <div className="activity-icon">
                      <FileText size={20} />
                    </div>
                    <div className="activity-content flex-1 min-w-0">
                      <p className="activity-title truncate">{signature.documento_titulo || 'Documento sin título'}</p>
                      <p className="activity-time break-words">
                        {formatDate(signature.fecha)}
                        {signature.firmantes && signature.firmantes.length > 0 && (
                          <span className="ml-2 text-gray-600">
                            • Firmado por: {signature.firmantes.join(', ')}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button 
                        onClick={async () => {
                          if (isSupervisor) {
                            toast.error('No tienes permisos para descargar documentos')
                            return
                          }
                          
                          try {
                            const token = localStorage.getItem('token') || sessionStorage.getItem('token')
                            if (!token) {
                              toast.error('No hay token de autenticación')
                              return
                            }
                            
                            const downloadUrl = `${API_BASE_URL}/api/download/${signature.id}`
                            
                            const response = await fetch(downloadUrl, {
                              headers: {
                                'Authorization': `Bearer ${token}`
                              }
                            })
                            
                            if (!response.ok) {
                              if (response.status === 401) {
                                toast.error('No autorizado. Por favor inicia sesión nuevamente')
                                return
                              }
                              if (response.status === 403) {
                                toast.error('No tienes permisos para descargar este documento')
                                return
                              }
                              if (response.status === 404) {
                                toast.error('Documento no encontrado')
                                return
                              }
                              throw new Error(`Error ${response.status}`)
                            }
                            
                            const blob = await response.blob()
                            const blobUrl = window.URL.createObjectURL(blob)
                            
                            // Obtener nombre del archivo del header Content-Disposition si está disponible
                            const contentDisposition = response.headers.get('Content-Disposition')
                            let filename = `documento_firmado_${signature.id}.pdf`
                            if (contentDisposition) {
                              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                              if (filenameMatch && filenameMatch[1]) {
                                filename = filenameMatch[1].replace(/['"]/g, '')
                                // Decodificar si está codificado
                                try {
                                  filename = decodeURIComponent(filename)
                                } catch (e) {
                                  // Si falla la decodificación, usar el nombre tal cual
                                }
                              }
                            }
                            
                            const downloadLink = document.createElement('a')
                            downloadLink.href = blobUrl
                            downloadLink.download = filename
                            downloadLink.style.position = 'fixed'
                            downloadLink.style.top = '-9999px'
                            downloadLink.style.left = '-9999px'
                            downloadLink.style.opacity = '0'
                            downloadLink.style.pointerEvents = 'none'
                            
                            document.body.appendChild(downloadLink)
                            downloadLink.click()
                            
                            // Limpiar después de un breve delay
                            setTimeout(() => {
                              window.URL.revokeObjectURL(blobUrl)
                              if (document.body.contains(downloadLink)) {
                                document.body.removeChild(downloadLink)
                              }
                            }, 100)
                            
                            toast.success('Documento descargado exitosamente')
                          } catch (error) {
                            console.error('Error al descargar:', error)
                            toast.error('Error al descargar: ' + (error.message || 'Error desconocido'))
                          }
                        }} 
                        className="text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
                        disabled={isSupervisor}
                        title={isSupervisor ? 'No tienes permisos para descargar documentos' : 'Descargar documento'}
                      >
                        <Download className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium hidden sm:inline">Download</span>
                        <span className="text-sm font-medium sm:hidden">Descargar</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <HelpButton tourId="dashboard-page" id="tour-dashboard-help" />
    </div>
  )
}

// Componentes de gráficos fuera del componente principal para mejor rendimiento
const LineChart = React.memo(({ data = [] }) => {
    const width = 700
    const height = 220
    const padding = 30
    const maxY = Math.max(1, ...data.map(d => d.cantidad))
    const stepX = (width - padding * 2) / Math.max(1, data.length - 1)
    const points = data.map((d, i) => {
      const x = padding + i * stepX
      const y = height - padding - (d.cantidad / maxY) * (height - padding * 2)
      return `${x},${y}`
    }).join(' ')
    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-56">
        <polyline fill="#E5ECFF" stroke="none" points={`${points} ${padding},${height - padding}`} />
        <polyline fill="none" stroke="#4F46E5" strokeWidth="2" points={points} />
        {data.map((d, i) => {
          const x = padding + i * stepX
          const y = height - padding - (d.cantidad / maxY) * (height - padding * 2)
          return <circle key={i} cx={x} cy={y} r="3" fill="#4F46E5" />
        })}
      </svg>
    )
})

export default Dashboard

